/**
 * StreamSyncEngage SaaS Client - Main Entry Point
 *
 * Initializes Navigo router, mvvLegit authentication, and pub/sub event system.
 *
 * NOTE: mvvLegit is provided globally by Neodigm 55 library (loaded in index.html)
 * It handles:
 * - JWT token management and automatic injection into fetch requests
 * - Route protection and navigation control
 * - Authentication state persistence in compressed localStorage
 */

// Import shared styles (design tokens, reset, icons, launCh)
import '../../../shared/styles/index.css';

import Navigo from 'navigo';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../shared/appBroadcast.js';
import { installPubSubVCR } from '../../../shared/pubsubVCR.js';
import { initAppEvents } from './core/app_events.js';
import AuthController from './services/AuthController.js';
import { I18nController } from './controllers/i18nController.js';
import LLMController from './services/llmController.js';
import MachVive from '../../../shared/machvive.js';

// Wrap PubSub.publish before anything broadcasts - console hooks startVCR() /
// stopVCR() capture a pub/sub timeline for remote support debugging.
installPubSubVCR(PubSub, { app: 'client-saas' });

// Initialize m5t namespace for shared components
window.m5t = Object.create(null);
window.m5t.pubsub = PubSub;

// mvvLegit is available globally from Neodigm 55 library
// Access it via window.mvvLegit

console.log('StreamSyncEngage SaaS Client - Starting...');
console.log('Environment:', import.meta.env.MODE);
console.log('Document readyState:', document.readyState);

// Debug: Check all possible ways to access mvvLegit
console.log('window.mvvLegit:', typeof window.mvvLegit);
console.log('globalThis.mvvLegit:', typeof globalThis.mvvLegit);
try {
  console.log('mvvLegit (direct):', typeof mvvLegit);
} catch (e) {
  console.log('mvvLegit (direct): ReferenceError -', e.message);
}

// List all globals that might be related
const possibleGlobals = Object.keys(window).filter(k =>
  k.toLowerCase().includes('mvv') ||
  k.toLowerCase().includes('neodigm') ||
  k.toLowerCase().includes('legit')
);
console.log('Possible Neodigm globals:', possibleGlobals);

// ============================================
// Router Configuration
// ============================================

const router = new Navigo('/', { hash: true });

// Expose router globally for use in event handlers and route modules
window.router = router;

/**
 * Check route access before navigation
 * Converts URL path to route token format expected by mvvLegit
 */
function checkRouteAccess(routePath) {
  // Convert path to route token (replace hyphens with underscores and add _route suffix)
  const routeToken = routePath ? `${routePath.replace(/-/g, '_')}_route` : 'splash_route';

  console.log(`[Router] Checking access: ${routePath} -> ${routeToken}`);

  // Check with mvvLegit
  const isAllowed = mvvLegit.isRouteAllowed(routeToken);

  if (!isAllowed) {
    console.warn(`[Router] Access denied to route: ${routeToken}`);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Please sign in to access this page',
      type: 'warning'
    });
    return false;
  }

  return true;
}

/**
 * Navigate to route by name
 */
function navigateToRoute(routeName) {
  console.log('[Router] Navigating to:', routeName);
  router.navigate(`/${routeName}`);
}

// ============================================
// Route Definitions
// ============================================

router.on('/', () => {
  console.log('[Router] Route: /');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'splash' });

  // Load splash route
  import('./routes/splash-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'splash' });
    })
    .catch(err => {
      console.error('[Router] Error loading splash route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'splash', error: err });
    });
});

router.on('/splash', () => {
  console.log('[Router] Route: /splash');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'splash' });

  import('./routes/splash-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'splash' });
    })
    .catch(err => {
      console.error('[Router] Error loading splash route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'splash', error: err });
    });
});

router.on('/signin', () => {
  console.log('[Router] Route: /signin');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signin' });

  import('./routes/signin-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signin' });
    })
    .catch(err => {
      console.error('[Router] Error loading signin route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signin', error: err });
    });
});

router.on('/signup', () => {
  console.log('[Router] Route: /signup');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signup' });

  import('./routes/signup-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signup' });
    })
    .catch(err => {
      console.error('[Router] Error loading signup route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signup', error: err });
    });
});

router.on('/verf-link', () => {
  console.log('[Router] Route: /verf-link');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'verf-link' });

  import('./routes/verf-link-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'verf-link' });
    })
    .catch(err => {
      console.error('[Router] Error loading verf-link route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'verf-link', error: err });
    });
});

router.on('/forgot', () => {
  console.log('[Router] Route: /forgot');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'forgot' });

  import('./routes/forgot-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'forgot' });
    })
    .catch(err => {
      console.error('[Router] Error loading forgot route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'forgot', error: err });
    });
});

router.on('/resethash', () => {
  console.log('[Router] Route: /resethash');

  // Check authentication (will convert to resethash_route internally)
  if (!checkRouteAccess('resethash')) {
    navigateToRoute('signin');
    return;
  }

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'resethash' });

  import('./routes/resethash-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'resethash' });
    })
    .catch(err => {
      console.error('[Router] Error loading resethash route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'resethash', error: err });
    });
});

router.on('/home', () => {
  console.log('[Router] Route: /home');

  // Check authentication (will convert to home_route internally)
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'home' });

  import('./routes/home-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'home' });
    })
    .catch(err => {
      console.error('[Router] Error loading home route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'home', error: err });
    });
});

router.on('/signout', () => {
  console.log('[Router] Route: /signout');

  // Check authentication (will convert to signout_route internally)
  if (!checkRouteAccess('signout')) {
    navigateToRoute('signin');
    return;
  }

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signout' });

  import('./routes/signout-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signout' });
    })
    .catch(err => {
      console.error('[Router] Error loading signout route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signout', error: err });
    });
});

router.notFound(() => {
  console.log('[Router] Route not found - redirecting to splash');
  navigateToRoute('splash');
});

// ============================================
// PubSub Navigation Handler
// ============================================

// Subscribe to ROUTE_NAV events and trigger navigation
PubSub.subscribe(AppBroadcast.ROUTE_NAV, (_msg, data) => {
  console.log('[Router] ROUTE_NAV event received:', data);
  if (data?.route) {
    navigateToRoute(data.route);
  }
});

// ============================================
// Initialize Application
// ============================================

// Wait for Neodigm 55 library to load, then initialize
// Note: We need to wait for both DOM and external scripts
function waitForNeodigm() {
  console.log('[App] Waiting for Neodigm 55 library...');

  let attempts = 0;
  const maxAttempts = 100; // 10 seconds (100 * 100ms)

  const checkMvvLegit = setInterval(() => {
    attempts++;

    // Log debug info every 10 attempts (1 second)
    if (attempts % 10 === 0) {
      console.log(`[App] Still waiting... attempt ${attempts}/100`, {
        mvvLegitExists: typeof mvvLegit !== 'undefined',
        neodigmLoaded: window.__neodigmLoaded
      });
    }

    if (typeof mvvLegit !== 'undefined') {
      console.log(`[App] Neodigm 55 loaded after ${attempts * 100}ms`);
      clearInterval(checkMvvLegit);
      initializeApp();
    } else if (attempts >= maxAttempts) {
      clearInterval(checkMvvLegit);
      console.error('[App] Failed to load Neodigm 55 library after 10 seconds');
      console.error('[App] Check browser console for CORS or network errors');

      // Show error in UI
      document.getElementById('app').innerHTML = `
        <div style="padding: 2rem; text-align: center; font-family: sans-serif;">
          <h2 style="color: #f44336;">Failed to Load Authentication Library</h2>
          <p>The Neodigm 55 library could not be loaded from the CDN.</p>
          <p>Please check your internet connection and try refreshing the page.</p>
          <button onclick="location.reload()" style="padding: 10px 20px; font-size: 1rem; cursor: pointer;">
            Refresh Page
          </button>
        </div>
      `;

      PubSub.publish(AppBroadcast.APP_ERROR, {
        message: 'Failed to load authentication library'
      });
    }
  }, 100);
}

// Start waiting as soon as possible
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForNeodigm);
} else {
  // DOM already loaded
  waitForNeodigm();
}

/**
 * Initialize application after Neodigm 55 is loaded
 */
async function initializeApp() {
  console.log('[App] Neodigm 55 loaded, initializing...');

  // Initialize event system
  initAppEvents();

  // Note: MachVive SSE to PubSub bridge is initialized automatically by mvvSSEther
  // mvvSSEther detects MachVive and calls MachVive.init() when snippet loads
  // mvvSSEther auto-connects 16 seconds after page load

  // Initialize i18n Controller (must be before route navigation)
  I18nController.initialize();
  console.log('[App] I18n Controller initialized');

  // Initialize LLMController (WebLLM for local AI)
  // This runs in background - doesn't block app startup
  LLMController.initialize()
    .then((success) => {
      if (success) {
        console.log('[App] LLM Controller initialized successfully');
      } else {
        console.warn('[App] LLM Controller initialization failed (non-critical)');
      }
    })
    .catch((err) => {
      console.error('[App] LLM Controller initialization error:', err);
    });

  // Initialize AuthController (replaces direct mvvLegit initialization)
  const authInitialized = await AuthController.init({
    // RULE: All client apps point to hosted API (no localhost fallback)
    BASE_URL: 'https://streamsyncengage-saas.onrender.com',
    navigateFunction: (routePath) => {
      console.log('[AuthController] Navigation requested:', routePath);
      navigateToRoute(routePath);
    }
  });

  if (!authInitialized) {
    console.error('[App] AuthController initialization failed');
    return;
  }

  // Note: AuthController already handles mvvLegit state changes internally
  // The APP_AUTH event will be published by AuthController's state callback if needed

  // Load persisted session user from localStorage (AuthController handles this)
  const sessionUser = AuthController.getEntity();
  if (sessionUser) {
    console.log('[App] Session restored for:', sessionUser.email);
    // Publish entity loaded event for other components to use
    PubSub.publish(AppBroadcast.APP_USER_LOADED, { entity: sessionUser });
  }

  // Publish app ready event
  PubSub.publish(AppBroadcast.APP_READY, {
    timestamp: new Date().toISOString(),
    sessionUser
  });

  // Resolve routes
  router.resolve();

  // Expose controllers globally for debugging and language switching
  window.AuthController = AuthController;
  window.I18nController = I18nController;
  window.LLMController = LLMController;
  window.MachVive = MachVive;
  console.log('[App] StreamSyncEngage client initialized (AuthController, I18nController, LLMController, MachVive available globally)');
}

// Subscribe to route navigation events to trigger i18n refresh
PubSub.subscribe(AppBroadcast.ROUTE_NAV_AFTER, (_msg, data) => {
  // Trigger i18n refresh after route navigation completes
  if (typeof I18nController !== 'undefined' && I18nController.refresh) {
    setTimeout(() => {
      I18nController.refresh();
    }, 100); // Small delay to ensure DOM is updated
  }
});
